import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent } from "vue"

const _hoisted_1 = { class: "bg-agent flex size-8 shrink-0 items-center justify-center rounded-lg text-white" }
const _hoisted_2 = { class: "flex min-w-0 flex-col leading-tight" }
const _hoisted_3 = { class: "truncate text-xs text-muted-foreground" }
const _hoisted_4 = { class: "flex min-w-0 flex-col leading-tight" }
const _hoisted_5 = { class: "truncate text-sm font-medium" }
const _hoisted_6 = { class: "truncate text-xs text-muted-foreground" }

import { computed, watch, type Component } from 'vue'
import { useRoute } from 'vue-router'
import {
  Boxes,
  Database,
  FileCode,
  Link,
  Wallet,
  Gauge,
  Hammer,
  Layers2,
  ChevronLeft,
  Folder,
  Home,
  LayoutDashboard,
  Layers,
  Lightbulb,
  LogOut,
  Package,
  Server,
  ShieldAlert,
  Waypoints
} from 'lucide-vue-next'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail
} from '@/components/ui/sidebar'
import auth from '../../services/auth/auth'
import { isAdmin, isCostExplorerEditor } from '../../utils/adminUtils'
import { visibleIdpSection } from '../../config/navigation'
import { initialsOf } from '../../pages/idp/components/teamRow'
import { teamNav, workloadNav } from '../../services/idp/idpPageNav'
import {
  idpWorkspaceHint,
  idpWorkspaceTarget,
  rememberIdpScope,
  useIdpLastScope
} from '../../services/idp/idpLastScope'


export default /*@__PURE__*/_defineComponent({
  __name: 'IdpSidebar',
  setup(__props) {

const ICONS: Record<string, Component> = {
  '/idp/services': Server,
  '/idp/graph': Waypoints,
  '/idp/packages': Package
}

const route = useRoute()
const user = computed(() => auth.getUser())
const initials = computed(() => (user.value?.name ? initialsOf(user.value.name) : ''))

const tools = computed(
  () =>
    visibleIdpSection({
      isAdmin: isAdmin(user.value),
      canEditCostExplorer: isCostExplorerEditor(user.value)
    })?.tools ?? []
)
const catalog = computed(() => tools.value.find((t) => t.path === '/idp/services'))
const explore = computed(() => tools.value.filter((t) => t.path !== '/idp/services'))

const onTeam = computed(() => route.path === '/idp/team')

const WORKSPACE_TABS = [
  { tab: 'overview', label: 'Overview', icon: Gauge },
  { tab: 'build', label: 'Build', icon: Hammer },
  { tab: 'apidocs', label: 'API contracts', icon: FileCode },
  { tab: 'tiering', label: 'API tiering', icon: Layers2 },
  { tab: 'dependencies', label: 'Dependencies', icon: Waypoints }
] as const
const onWorkspace = computed(() => route.path === '/idp/workspace')
const workspaceTab = computed(() => {
  const t = typeof route.query.tab === 'string' ? route.query.tab : ''
  return WORKSPACE_TABS.some((x) => x.tab === t) ? t : 'overview'
})
const workspaceLink = (tab: string) => ({
  path: '/idp/workspace',
  query: { ...workloadNav.value?.query, tab }
})
// Scope carries across, like every other IDP link.
const teamLink = (view: 'insights' | 'overview') => ({
  path: '/idp/team',
  query: {
    ...(typeof route.query.env === 'string' ? { env: route.query.env } : {}),
    ...(typeof route.query.cluster === 'string' ? { cluster: route.query.cluster } : {}),
    team: teamNav.value?.key,
    ...(view === 'overview' ? { view } : {})
  }
})

const SUB_TEAM_VIEWS = [
  { view: 'insights', label: 'Actionable', icon: Lightbulb },
  { view: 'workloads', label: 'Workloads', icon: Boxes },
  { view: 'resources', label: 'Resources', icon: Database },
  { view: 'links', label: 'Links', icon: Link },
  { view: 'cost', label: 'Cost', icon: Wallet },
  { view: 'violations', label: 'IDOR Violations', icon: ShieldAlert }
] as const
const onSubTeam = computed(() => route.path === '/idp/subteam')
const subTeamView = computed(() => {
  const v = typeof route.query.view === 'string' ? route.query.view : ''
  return SUB_TEAM_VIEWS.some((x) => x.view === v) ? v : 'insights'
})
const subTeamLink = (view: string) => ({
  path: '/idp/subteam',
  query: {
    ...(typeof route.query.env === 'string' ? { env: route.query.env } : {}),
    ...(typeof route.query.cluster === 'string' ? { cluster: route.query.cluster } : {}),
    team: teamNav.value?.key,
    subTeam: teamNav.value?.subTeam?.key,
    ...(view === 'insights' ? {} : { view })
  }
})

const lastScope = useIdpLastScope()
const workspaceTo = computed(() => idpWorkspaceTarget(lastScope.value))
const workspaceHint = computed(() => idpWorkspaceHint(lastScope.value))

watch(
  () => [
    route.path,
    route.query.team,
    route.query.subTeam,
    route.query.service,
    route.query.env,
    route.query.cluster
  ],
  () => rememberIdpScope(route.query),
  { immediate: true }
)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(_unref(Sidebar), { collapsible: "icon" }, {
    default: _withCtx(() => [
      _createVNode(_unref(SidebarHeader), null, {
        default: _withCtx(() => [
          _createVNode(_unref(SidebarMenu), null, {
            default: _withCtx(() => [
              _createVNode(_unref(SidebarMenuItem), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarMenuButton), {
                    "as-child": "",
                    size: "lg",
                    tooltip: "IDP"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_router_link, { to: "/idp" }, {
                        default: _withCtx(() => [
                          _createElementVNode("span", _hoisted_1, [
                            _createVNode(_unref(Layers), { class: "size-4" })
                          ]),
                          _cache[1] || (_cache[1] = _createElementVNode("span", { class: "flex flex-col leading-tight" }, [
                            _createElementVNode("span", { class: "text-sm font-semibold" }, "IDP"),
                            _createElementVNode("span", { class: "text-xs text-muted-foreground" }, "HighLevel")
                          ], -1))
                        ]),
                        _: 1,
                        __: [1]
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(SidebarContent), null, {
        default: _withCtx(() => [
          _createVNode(_unref(SidebarGroup), null, {
            default: _withCtx(() => [
              _createVNode(_unref(SidebarGroupLabel), null, {
                default: _withCtx(() => _cache[2] || (_cache[2] = [
                  _createTextVNode("Platform")
                ])),
                _: 1,
                __: [2]
              }),
              _createVNode(_unref(SidebarGroupContent), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarMenu), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SidebarMenuItem), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(SidebarMenuButton), {
                            "as-child": "",
                            "is-active": _unref(route).path === '/idp',
                            tooltip: "Home"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_router_link, { to: "/idp" }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Home)),
                                  _cache[3] || (_cache[3] = _createElementVNode("span", null, "Home", -1))
                                ]),
                                _: 1,
                                __: [3]
                              })
                            ]),
                            _: 1
                          }, 8, ["is-active"])
                        ]),
                        _: 1
                      }),
                      (catalog.value)
                        ? (_openBlock(), _createBlock(_unref(SidebarMenuItem), { key: 0 }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SidebarMenuButton), {
                                "as-child": "",
                                "is-active": _unref(route).path === '/idp/services' || _unref(route).path === '/idp/team',
                                tooltip: catalog.value.name
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_router_link, {
                                    to: catalog.value.path
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Server)),
                                      _createElementVNode("span", null, _toDisplayString(catalog.value.name), 1)
                                    ]),
                                    _: 1
                                  }, 8, ["to"])
                                ]),
                                _: 1
                              }, 8, ["is-active", "tooltip"])
                            ]),
                            _: 1
                          }))
                        : _createCommentVNode("", true),
                      _createVNode(_unref(SidebarMenuItem), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(SidebarMenuButton), {
                            "as-child": "",
                            "is-active": _unref(route).path === '/idp/workspace' || _unref(route).path === '/idp/subteam',
                            tooltip: workspaceHint.value,
                            class: "h-auto"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_router_link, { to: workspaceTo.value }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(Folder)),
                                  _createElementVNode("span", _hoisted_2, [
                                    _cache[4] || (_cache[4] = _createElementVNode("span", { class: "truncate" }, "Workspace", -1)),
                                    _createElementVNode("span", _hoisted_3, _toDisplayString(workspaceHint.value), 1)
                                  ])
                                ]),
                                _: 1
                              }, 8, ["to"])
                            ]),
                            _: 1
                          }, 8, ["is-active", "tooltip"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          (onTeam.value && _unref(teamNav))
            ? (_openBlock(), _createBlock(_unref(SidebarGroup), { key: 0 }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarGroupLabel), { class: "truncate" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(teamNav).name), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SidebarGroupContent), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SidebarMenu), null, {
                        default: _withCtx(() => [
                          _createVNode(_unref(SidebarMenuItem), null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SidebarMenuButton), {
                                "as-child": "",
                                "is-active": _unref(route).query.view !== 'overview',
                                tooltip: "Actionable"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_router_link, {
                                    to: teamLink('insights')
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(Lightbulb)),
                                      _cache[5] || (_cache[5] = _createElementVNode("span", null, "Actionable", -1))
                                    ]),
                                    _: 1,
                                    __: [5]
                                  }, 8, ["to"])
                                ]),
                                _: 1
                              }, 8, ["is-active"]),
                              (_unref(teamNav).insights !== null)
                                ? (_openBlock(), _createBlock(_unref(SidebarMenuBadge), { key: 0 }, {
                                    default: _withCtx(() => [
                                      _createTextVNode(_toDisplayString(_unref(teamNav).insights), 1)
                                    ]),
                                    _: 1
                                  }))
                                : _createCommentVNode("", true)
                            ]),
                            _: 1
                          }),
                          _createVNode(_unref(SidebarMenuItem), null, {
                            default: _withCtx(() => [
                              _createVNode(_unref(SidebarMenuButton), {
                                "as-child": "",
                                "is-active": _unref(route).query.view === 'overview',
                                tooltip: "Overview"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_router_link, {
                                    to: teamLink('overview')
                                  }, {
                                    default: _withCtx(() => [
                                      _createVNode(_unref(LayoutDashboard)),
                                      _cache[6] || (_cache[6] = _createElementVNode("span", null, "Overview", -1))
                                    ]),
                                    _: 1,
                                    __: [6]
                                  }, 8, ["to"])
                                ]),
                                _: 1
                              }, 8, ["is-active"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (onSubTeam.value && _unref(teamNav)?.subTeam)
            ? (_openBlock(), _createBlock(_unref(SidebarGroup), { key: 1 }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarGroupLabel), { class: "truncate" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(teamNav).name) + " · " + _toDisplayString(_unref(teamNav).subTeam.name), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SidebarGroupContent), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SidebarMenu), null, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(SUB_TEAM_VIEWS, (v) => {
                            return _createVNode(_unref(SidebarMenuItem), {
                              key: v.view
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(SidebarMenuButton), {
                                  "as-child": "",
                                  "is-active": subTeamView.value === v.view,
                                  tooltip: v.label
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_router_link, {
                                      to: subTeamLink(v.view)
                                    }, {
                                      default: _withCtx(() => [
                                        (_openBlock(), _createBlock(_resolveDynamicComponent(v.icon))),
                                        _createElementVNode("span", null, _toDisplayString(v.label), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["to"])
                                  ]),
                                  _: 2
                                }, 1032, ["is-active", "tooltip"]),
                                (v.view === 'insights' && _unref(teamNav).insights !== null)
                                  ? (_openBlock(), _createBlock(_unref(SidebarMenuBadge), { key: 0 }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(_unref(teamNav).insights), 1)
                                      ]),
                                      _: 1
                                    }))
                                  : _createCommentVNode("", true)
                              ]),
                              _: 2
                            }, 1024)
                          }), 64))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          (onWorkspace.value && _unref(workloadNav))
            ? (_openBlock(), _createBlock(_unref(SidebarGroup), { key: 2 }, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarGroupLabel), { class: "truncate font-mono" }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(workloadNav).service), 1)
                    ]),
                    _: 1
                  }),
                  _createVNode(_unref(SidebarGroupContent), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SidebarMenu), null, {
                        default: _withCtx(() => [
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(WORKSPACE_TABS, (t) => {
                            return _createVNode(_unref(SidebarMenuItem), {
                              key: t.tab
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_unref(SidebarMenuButton), {
                                  "as-child": "",
                                  "is-active": workspaceTab.value === t.tab,
                                  tooltip: t.label
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_router_link, {
                                      to: workspaceLink(t.tab)
                                    }, {
                                      default: _withCtx(() => [
                                        (_openBlock(), _createBlock(_resolveDynamicComponent(t.icon))),
                                        _createElementVNode("span", null, _toDisplayString(t.label), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["to"])
                                  ]),
                                  _: 2
                                }, 1032, ["is-active", "tooltip"])
                              ]),
                              _: 2
                            }, 1024)
                          }), 64))
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_unref(SidebarGroup), null, {
            default: _withCtx(() => [
              _createVNode(_unref(SidebarGroupLabel), null, {
                default: _withCtx(() => _cache[7] || (_cache[7] = [
                  _createTextVNode("Explore")
                ])),
                _: 1,
                __: [7]
              }),
              _createVNode(_unref(SidebarGroupContent), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarMenu), null, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(explore.value, (tool) => {
                        return (_openBlock(), _createBlock(_unref(SidebarMenuItem), {
                          key: tool.path
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(SidebarMenuButton), {
                              "as-child": "",
                              "is-active": _unref(route).path === tool.path,
                              tooltip: tool.name
                            }, {
                              default: _withCtx(() => [
                                _createVNode(_component_router_link, {
                                  to: tool.path
                                }, {
                                  default: _withCtx(() => [
                                    (_openBlock(), _createBlock(_resolveDynamicComponent(ICONS[tool.path] ?? tool.icon))),
                                    _createElementVNode("span", null, _toDisplayString(tool.name), 1)
                                  ]),
                                  _: 2
                                }, 1032, ["to"])
                              ]),
                              _: 2
                            }, 1032, ["is-active", "tooltip"])
                          ]),
                          _: 2
                        }, 1024))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(SidebarFooter), null, {
        default: _withCtx(() => [
          _createVNode(_unref(SidebarMenu), null, {
            default: _withCtx(() => [
              _createVNode(_unref(SidebarMenuItem), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarMenuButton), {
                    "as-child": "",
                    tooltip: "Developer Tools"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_router_link, { to: "/" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(ChevronLeft)),
                          _cache[8] || (_cache[8] = _createElementVNode("span", null, "Developer Tools", -1))
                        ]),
                        _: 1,
                        __: [8]
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              (user.value)
                ? (_openBlock(), _createBlock(_unref(SidebarMenuItem), { key: 0 }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(SidebarMenuButton), {
                        size: "lg",
                        tooltip: user.value.email,
                        class: "cursor-default hover:bg-transparent active:bg-transparent"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(Avatar), { class: "size-8 rounded-lg" }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(AvatarFallback), { class: "rounded-lg bg-primary/10 text-xs font-semibold text-primary" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(initials.value), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }),
                          _createElementVNode("span", _hoisted_4, [
                            _createElementVNode("span", _hoisted_5, _toDisplayString(user.value.name), 1),
                            _createElementVNode("span", _hoisted_6, _toDisplayString(user.value.email), 1)
                          ])
                        ]),
                        _: 1
                      }, 8, ["tooltip"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              _createVNode(_unref(SidebarMenuItem), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(SidebarMenuButton), {
                    tooltip: "Logout",
                    class: "text-muted-foreground hover:text-critical",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(auth).logout()))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(LogOut)),
                      _cache[9] || (_cache[9] = _createElementVNode("span", null, "Logout", -1))
                    ]),
                    _: 1,
                    __: [9]
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_unref(SidebarRail))
    ]),
    _: 1
  }))
}
}

})